import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import {
    Trophy, Star, Gift, Target, Zap, Crown, Medal,
    Plus, Trash2, Check, Clock, Calendar, Sparkles,
    TrendingUp, Award, ChevronRight, X
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const ChallengesPage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [myPoints, setMyPoints] = useState({ total_points: 0, transactions: [] });
    const [rewards, setRewards] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [pointRules, setPointRules] = useState([]);
    const [adminOverview, setAdminOverview] = useState([]);
    const [achievements, setAchievements] = useState([]);
    const [achievementStats, setAchievementStats] = useState(null);
    const [showCreateReward, setShowCreateReward] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [adjustPoints, setAdjustPoints] = useState({ points: 0, reason: '' });
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [newReward, setNewReward] = useState({
        name: '',
        description: '',
        points_required: 50,
        emoji: '🎁'
    });

    const isAdmin = user?.role === 'chef';
    
    const categoryLabels = {
        task: { name: 'Tasks', emoji: '📋' },
        project: { name: 'Projekte', emoji: '📁' },
        time: { name: 'Zeit', emoji: '⏱' },
        chat: { name: 'Chat & Team', emoji: '💬' },
        vacation: { name: 'Urlaub', emoji: '🌴' },
        level: { name: 'Level', emoji: '🏆' },
        special: { name: 'Spezial', emoji: '🔥' },
        hidden: { name: 'Geheim', emoji: '💎' },
        team: { name: 'Team', emoji: '🎮' }
    };

    const fetchData = useCallback(async () => {
        try {
            const [pointsRes, rewardsRes, leaderboardRes, rulesRes, achievementsRes, statsRes] = await Promise.all([
                api.get('/challenges/my-points'),
                api.get('/challenges/rewards'),
                api.get('/challenges/leaderboard'),
                api.get('/challenges/point-rules'),
                api.get('/achievements'),
                api.get('/achievements/stats')
            ]);
            
            setMyPoints(pointsRes.data);
            setRewards(rewardsRes.data);
            setLeaderboard(leaderboardRes.data);
            setPointRules(rulesRes.data.rules);
            setAchievements(achievementsRes.data);
            setAchievementStats(statsRes.data);

            // Check daily login bonus
            try {
                const loginRes = await api.post('/challenges/check-daily-login');
                if (loginRes.data.awarded) {
                    toast.success('🌟 +1 Punkt für täglichen Login!');
                    confetti({ particleCount: 30, spread: 50 });
                    setMyPoints(prev => ({
                        ...prev,
                        total_points: prev.total_points + 1
                    }));
                }
            } catch (e) {}
            
            // Trigger achievement check
            try {
                await api.post('/achievements/check');
            } catch (e) {}

            if (isAdmin) {
                const overviewRes = await api.get('/challenges/admin/overview');
                setAdminOverview(overviewRes.data);
            }
        } catch (error) {
            console.error('Error fetching challenges data:', error);
        } finally {
            setLoading(false);
        }
    }, [api, isAdmin]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRedeemReward = async (reward) => {
        if (myPoints.total_points < reward.points_required) {
            toast.error(`Nicht genug Punkte! Du brauchst ${reward.points_required} Punkte.`);
            return;
        }
        
        if (!window.confirm(`${reward.emoji} ${reward.name} für ${reward.points_required} Punkte einlösen?`)) return;
        
        try {
            await api.post(`/challenges/redeem/${reward.id}`);
            toast.success(`🎉 ${reward.name} eingelöst!`);
            confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Einlösen');
        }
    };

    const handleCreateReward = async () => {
        try {
            await api.post('/challenges/rewards', newReward);
            toast.success('Belohnung erstellt!');
            setShowCreateReward(false);
            setNewReward({ name: '', description: '', points_required: 50, emoji: '🎁' });
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Erstellen');
        }
    };

    const handleDeleteReward = async (rewardId) => {
        if (!window.confirm('Belohnung löschen?')) return;
        try {
            await api.delete(`/challenges/rewards/${rewardId}`);
            toast.success('Belohnung gelöscht');
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleAdjustPoints = async () => {
        if (!selectedUser || !adjustPoints.points || !adjustPoints.reason) return;
        try {
            await api.post(`/challenges/award-points?user_id=${selectedUser.user.id}&points=${adjustPoints.points}&reason=${encodeURIComponent(adjustPoints.reason)}`);
            toast.success('Punkte angepasst!');
            setSelectedUser(null);
            setAdjustPoints({ points: 0, reason: '' });
            fetchData();
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const myRank = leaderboard.findIndex(u => u.user_id === user?.id) + 1;
    const nextReward = rewards.find(r => r.points_required > myPoints.total_points);
    const progressToNext = nextReward 
        ? (myPoints.total_points / nextReward.points_required) * 100 
        : 100;

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="challenges-page">
            {/* Hero Section */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative mb-8 p-6 md:p-8 rounded-3xl bg-gradient-to-br from-yellow-500/20 via-orange-500/10 to-red-500/10 border border-yellow-500/30 overflow-hidden"
            >
                <div className="absolute top-4 right-4 text-6xl opacity-20">🏆</div>
                <div className="relative">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                            <Trophy className="w-7 h-7 text-white" />
                        </div>
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Challenges
                            </h1>
                            <p className="text-muted-foreground">Sammle Punkte & gewinne Belohnungen!</p>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                        {/* Points */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="p-4 rounded-2xl bg-background/80 backdrop-blur"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Star className="w-5 h-5 text-yellow-500" />
                                <span className="text-sm text-muted-foreground">Deine Punkte</span>
                            </div>
                            <p className="text-4xl font-bold text-yellow-500">{myPoints.total_points}</p>
                        </motion.div>
                        
                        {/* Rank */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="p-4 rounded-2xl bg-background/80 backdrop-blur"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Medal className="w-5 h-5 text-orange-500" />
                                <span className="text-sm text-muted-foreground">Dein Rang</span>
                            </div>
                            <p className="text-4xl font-bold">
                                #{myRank || '—'}
                                {myRank === 1 && <span className="text-2xl ml-2">👑</span>}
                            </p>
                        </motion.div>
                        
                        {/* Next Reward */}
                        <motion.div
                            whileHover={{ scale: 1.02 }}
                            className="p-4 rounded-2xl bg-background/80 backdrop-blur"
                        >
                            <div className="flex items-center gap-2 mb-2">
                                <Gift className="w-5 h-5 text-pink-500" />
                                <span className="text-sm text-muted-foreground">Nächste Belohnung</span>
                            </div>
                            {nextReward ? (
                                <>
                                    <p className="font-semibold truncate">{nextReward.emoji} {nextReward.name}</p>
                                    <Progress value={progressToNext} className="mt-2 h-2" />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {myPoints.total_points}/{nextReward.points_required} Punkte
                                    </p>
                                </>
                            ) : (
                                <p className="text-sm text-muted-foreground">Alle erreicht! 🎉</p>
                            )}
                        </motion.div>
                    </div>
                </div>
            </motion.div>

            <Tabs defaultValue="rewards" className="space-y-6">
                <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
                    <TabsTrigger value="rewards" className="gap-2">
                        <Gift className="w-4 h-4" />
                        Belohnungen
                    </TabsTrigger>
                    <TabsTrigger value="achievements" className="gap-2" data-testid="achievements-tab">
                        <Award className="w-4 h-4" />
                        Achievements
                    </TabsTrigger>
                    <TabsTrigger value="rules" className="gap-2">
                        <Target className="w-4 h-4" />
                        Punkte sammeln
                    </TabsTrigger>
                    <TabsTrigger value="leaderboard" className="gap-2">
                        <Trophy className="w-4 h-4" />
                        Rangliste
                    </TabsTrigger>
                    {isAdmin && (
                        <TabsTrigger value="admin" className="gap-2">
                            <Crown className="w-4 h-4" />
                            Admin
                        </TabsTrigger>
                    )}
                </TabsList>

                {/* Achievements Tab */}
                <TabsContent value="achievements" className="space-y-6" data-testid="achievements-content">
                    {/* Stats Overview */}
                    {achievementStats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="p-4 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-orange-500/10 border border-yellow-500/30"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Award className="w-5 h-5 text-yellow-500" />
                                    <span className="text-sm text-muted-foreground">Erreicht</span>
                                </div>
                                <p className="text-3xl font-bold">{achievementStats.earned_achievements}</p>
                                <p className="text-xs text-muted-foreground">von {achievementStats.total_achievements}</p>
                            </motion.div>
                            
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 }}
                                className="p-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/10 border border-purple-500/30"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Crown className="w-5 h-5 text-purple-500" />
                                    <span className="text-sm text-muted-foreground">Level</span>
                                </div>
                                <p className="text-3xl font-bold">{achievementStats.level}</p>
                                <p className="text-xs text-muted-foreground">{achievementStats.total_points} Punkte</p>
                            </motion.div>
                            
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                                className="p-4 rounded-2xl bg-gradient-to-br from-green-500/20 to-emerald-500/10 border border-green-500/30"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <TrendingUp className="w-5 h-5 text-green-500" />
                                    <span className="text-sm text-muted-foreground">Fortschritt</span>
                                </div>
                                <p className="text-3xl font-bold">{Math.round(achievementStats.completion_percent)}%</p>
                                <Progress value={achievementStats.completion_percent} className="mt-2 h-1.5" />
                            </motion.div>
                            
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="p-4 rounded-2xl bg-gradient-to-br from-blue-500/20 to-cyan-500/10 border border-blue-500/30"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Sparkles className="w-5 h-5 text-blue-500" />
                                    <span className="text-sm text-muted-foreground">Bonus erhalten</span>
                                </div>
                                <p className="text-3xl font-bold">+{achievementStats.earned_achievements * 10}</p>
                                <p className="text-xs text-muted-foreground">Punkte durch Badges</p>
                            </motion.div>
                        </div>
                    )}
                    
                    {/* Category Filter */}
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant={selectedCategory === 'all' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setSelectedCategory('all')}
                            className="rounded-full"
                        >
                            Alle
                        </Button>
                        {Object.entries(categoryLabels).map(([key, { name, emoji }]) => (
                            <Button
                                key={key}
                                variant={selectedCategory === key ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setSelectedCategory(key)}
                                className="rounded-full gap-1"
                            >
                                <span>{emoji}</span>
                                {name}
                            </Button>
                        ))}
                    </div>
                    
                    {/* Achievements Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <AnimatePresence mode="popLayout">
                            {achievements
                                .filter(a => selectedCategory === 'all' || a.category === selectedCategory)
                                .sort((a, b) => {
                                    // Earned first, then sort by category to mix hidden with others
                                    if (a.earned && !b.earned) return -1;
                                    if (!a.earned && b.earned) return 1;
                                    // Mix by category alphabetically, then by progress within category
                                    if (a.category !== b.category) {
                                        return a.category.localeCompare(b.category);
                                    }
                                    return b.progress_percent - a.progress_percent;
                                })
                                .map((achievement, index) => (
                                    <motion.div
                                        key={achievement.id}
                                        layout
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ delay: index * 0.02 }}
                                    >
                                        <Card 
                                            className={`relative overflow-hidden transition-all duration-300 ${
                                                achievement.earned 
                                                    ? 'border-yellow-500/50 bg-gradient-to-br from-yellow-500/10 to-orange-500/5 shadow-lg shadow-yellow-500/10' 
                                                    : achievement.hidden && !achievement.earned
                                                        ? 'border-gray-500/30 bg-gray-500/5'
                                                        : 'hover:border-primary/30'
                                            }`}
                                            data-testid={`achievement-${achievement.id}`}
                                        >
                                            {/* Earned Badge */}
                                            {achievement.earned && (
                                                <div className="absolute top-2 right-2">
                                                    <div className="w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center">
                                                        <Check className="w-4 h-4 text-white" />
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <CardContent className={`p-4 ${achievement.hidden && !achievement.earned ? 'blur-sm select-none' : ''}`}>
                                                <div className="flex items-start gap-3">
                                                    <div className={`text-4xl transition-transform ${achievement.earned ? 'scale-110' : 'grayscale opacity-60'}`}>
                                                        {achievement.emoji}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className={`font-semibold truncate ${!achievement.earned && 'text-muted-foreground'}`}>
                                                                {achievement.name}
                                                            </h3>
                                                            <Badge variant="outline" className="text-xs shrink-0">
                                                                {categoryLabels[achievement.category]?.emoji} {categoryLabels[achievement.category]?.name}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground mt-1">
                                                            {achievement.description}
                                                        </p>
                                                        
                                                        {!achievement.earned && (
                                                            <div className="mt-3">
                                                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                                                    <span>Fortschritt</span>
                                                                    <span>{achievement.progress} / {achievement.requirement}</span>
                                                                </div>
                                                                <Progress 
                                                                    value={achievement.progress_percent} 
                                                                    className="h-2"
                                                                />
                                                            </div>
                                                        )}
                                                        
                                                        {achievement.earned && (
                                                            <div className="mt-3 flex items-center gap-2">
                                                                <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">
                                                                    <Star className="w-3 h-3 mr-1" />
                                                                    +{achievement.points_reward} Punkte
                                                                </Badge>
                                                                <span className="text-xs text-muted-foreground">
                                                                    {achievement.earned_at && format(new Date(achievement.earned_at), 'PP', { locale: de })}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </CardContent>
                                            
                                            {/* Hidden overlay */}
                                            {achievement.hidden && !achievement.earned && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm">
                                                    <div className="text-center">
                                                        <div className="text-3xl mb-2">🔒</div>
                                                        <p className="text-sm font-medium">Geheimes Achievement</p>
                                                        <p className="text-xs text-muted-foreground">Finde heraus wie!</p>
                                                    </div>
                                                </div>
                                            )}
                                        </Card>
                                    </motion.div>
                                ))}
                        </AnimatePresence>
                    </div>
                    
                    {achievements.filter(a => selectedCategory === 'all' || a.category === selectedCategory).length === 0 && (
                        <div className="text-center py-12">
                            <Award className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                            <p className="text-muted-foreground">Keine Achievements in dieser Kategorie</p>
                        </div>
                    )}
                </TabsContent>

                {/* Rewards Tab */}
                <TabsContent value="rewards" className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold">🎁 Verfügbare Belohnungen</h2>
                        {isAdmin && (
                            <Button onClick={() => setShowCreateReward(true)} data-testid="create-reward-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Neue Belohnung
                            </Button>
                        )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <AnimatePresence>
                            {rewards.map((reward, index) => {
                                const canRedeem = myPoints.total_points >= reward.points_required;
                                return (
                                    <motion.div
                                        key={reward.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        <Card className={`relative overflow-hidden ${canRedeem ? 'border-green-500/50 bg-green-500/5' : ''}`}>
                                            <CardContent className="p-4">
                                                <div className="text-4xl mb-3">{reward.emoji}</div>
                                                <h3 className="font-semibold mb-1">{reward.name}</h3>
                                                {reward.description && (
                                                    <p className="text-sm text-muted-foreground mb-3">{reward.description}</p>
                                                )}
                                                <div className="flex items-center justify-between">
                                                    <Badge variant="outline" className="text-yellow-600">
                                                        <Star className="w-3 h-3 mr-1" />
                                                        {reward.points_required} Punkte
                                                    </Badge>
                                                    <Button
                                                        size="sm"
                                                        disabled={!canRedeem}
                                                        onClick={() => handleRedeemReward(reward)}
                                                        className={canRedeem ? 'bg-green-500 hover:bg-green-600' : ''}
                                                        data-testid={`redeem-${reward.id}`}
                                                    >
                                                        {canRedeem ? 'Einlösen' : 'Gesperrt'}
                                                    </Button>
                                                </div>
                                                {isAdmin && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="absolute top-2 right-2 h-7 w-7 text-destructive"
                                                        onClick={() => handleDeleteReward(reward.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>
                    
                    {rewards.length === 0 && (
                        <div className="text-center py-12">
                            <Gift className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                            <p className="text-muted-foreground">Noch keine Belohnungen erstellt</p>
                        </div>
                    )}
                </TabsContent>

                {/* Rules Tab */}
                <TabsContent value="rules" className="space-y-4">
                    <h2 className="text-xl font-semibold">⚡ So sammelst du Punkte</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {pointRules.map((rule, index) => (
                            <motion.div
                                key={rule.action}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card className="overflow-hidden">
                                    <CardContent className="p-4 flex items-center gap-4">
                                        <div className="text-4xl">{rule.emoji}</div>
                                        <div className="flex-1">
                                            <h3 className="font-semibold">{rule.action}</h3>
                                        </div>
                                        <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-lg px-3 py-1">
                                            +{rule.points}
                                        </Badge>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                    
                    {/* Recent Activity */}
                    <div className="mt-8">
                        <h3 className="font-semibold mb-4">📜 Deine letzten Aktivitäten</h3>
                        <Card>
                            <CardContent className="p-4">
                                {myPoints.transactions.length > 0 ? (
                                    <div className="space-y-3">
                                        {myPoints.transactions.slice(0, 10).map((tx) => (
                                            <div key={tx.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.points > 0 ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                                                        {tx.points > 0 ? <TrendingUp className="w-4 h-4" /> : <Gift className="w-4 h-4" />}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium">{tx.reason}</p>
                                                        <p className="text-xs text-muted-foreground">
                                                            {format(new Date(tx.created_at), 'PPp', { locale: de })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <Badge variant={tx.points > 0 ? "default" : "secondary"} className={tx.points > 0 ? 'bg-green-500' : ''}>
                                                    {tx.points > 0 ? '+' : ''}{tx.points}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-muted-foreground py-4">
                                        Noch keine Aktivitäten. Starte mit deinem ersten Task!
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                {/* Leaderboard Tab */}
                <TabsContent value="leaderboard" className="space-y-4">
                    <h2 className="text-xl font-semibold">🏆 Team Rangliste</h2>
                    
                    <Card>
                        <CardContent className="p-4">
                            <div className="space-y-2">
                                {leaderboard.map((entry, index) => {
                                    const isMe = entry.user_id === user?.id;
                                    return (
                                        <motion.div
                                            key={entry.user_id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className={`flex items-center gap-4 p-3 rounded-xl ${isMe ? 'bg-primary/10 border border-primary/30' : 'hover:bg-muted/50'}`}
                                        >
                                            <div className="w-8 text-center font-bold text-lg">
                                                {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                                            </div>
                                            <Avatar>
                                                <AvatarFallback style={{ backgroundColor: entry.profile_color }} className="text-white">
                                                    {entry.first_name?.[0]}{entry.last_name?.[0]}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1">
                                                <p className="font-medium">
                                                    {entry.first_name} {entry.last_name}
                                                    {isMe && <Badge className="ml-2 text-xs">Du</Badge>}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Star className="w-4 h-4 text-yellow-500" />
                                                <span className="font-bold text-lg">{entry.points}</span>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Admin Tab */}
                {isAdmin && (
                    <TabsContent value="admin" className="space-y-4">
                        <h2 className="text-xl font-semibold">👑 Admin Übersicht</h2>
                        
                        <div className="grid gap-4">
                            {adminOverview.map((item) => (
                                <Card key={item.user.id}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <Avatar>
                                                    <AvatarFallback style={{ backgroundColor: item.user.profile_color }} className="text-white">
                                                        {item.user.first_name?.[0]}{item.user.last_name?.[0]}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="font-semibold">{item.user.first_name} {item.user.last_name}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {item.total_points} Punkte • {item.redemptions.length} Einlösungen
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setSelectedUser(item)}
                                            >
                                                Punkte anpassen
                                            </Button>
                                        </div>
                                        
                                        {item.redemptions.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-border/50">
                                                <p className="text-sm font-medium mb-2">Letzte Einlösungen:</p>
                                                <div className="space-y-1">
                                                    {item.redemptions.slice(0, 3).map((r) => (
                                                        <div key={r.id} className="flex items-center justify-between text-sm">
                                                            <span>{r.reward_name}</span>
                                                            <span className="text-muted-foreground">
                                                                {format(new Date(r.created_at), 'PP', { locale: de })}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                )}
            </Tabs>

            {/* Create Reward Modal */}
            <Dialog open={showCreateReward} onOpenChange={setShowCreateReward}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Neue Belohnung erstellen</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-4 gap-2">
                            {['🎁', '☕', '🍕', '🎬', '🏠', '💰', '🎮', '✈️'].map((e) => (
                                <Button
                                    key={e}
                                    variant={newReward.emoji === e ? 'default' : 'outline'}
                                    className="text-2xl h-12"
                                    onClick={() => setNewReward({ ...newReward, emoji: e })}
                                >
                                    {e}
                                </Button>
                            ))}
                        </div>
                        <div>
                            <Label>Name</Label>
                            <Input
                                value={newReward.name}
                                onChange={(e) => setNewReward({ ...newReward, name: e.target.value })}
                                placeholder="z.B. 1h früher gehen"
                            />
                        </div>
                        <div>
                            <Label>Beschreibung (optional)</Label>
                            <Textarea
                                value={newReward.description}
                                onChange={(e) => setNewReward({ ...newReward, description: e.target.value })}
                                placeholder="Details zur Belohnung..."
                            />
                        </div>
                        <div>
                            <Label>Benötigte Punkte</Label>
                            <div className="mt-3 space-y-3">
                                <input
                                    type="range"
                                    min="0"
                                    max="8000"
                                    step="10"
                                    value={newReward.points_required}
                                    onChange={(e) => setNewReward({ ...newReward, points_required: parseInt(e.target.value) })}
                                    className="w-full h-2 bg-muted rounded-full appearance-none cursor-pointer accent-primary"
                                    data-testid="points-cost-slider"
                                />
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl font-bold">{newReward.points_required}</span>
                                        <span className="text-sm text-muted-foreground">Punkte</span>
                                    </div>
                                    <Badge className={`text-xs ${
                                        newReward.points_required <= 50 ? 'bg-green-100 text-green-700' :
                                        newReward.points_required <= 150 ? 'bg-emerald-100 text-emerald-700' :
                                        newReward.points_required <= 500 ? 'bg-yellow-100 text-yellow-700' :
                                        newReward.points_required <= 1500 ? 'bg-orange-100 text-orange-700' :
                                        newReward.points_required <= 4000 ? 'bg-red-100 text-red-700' :
                                        'bg-purple-100 text-purple-700'
                                    }`}>
                                        {newReward.points_required <= 50 ? 'Leicht' :
                                         newReward.points_required <= 150 ? 'Machbar' :
                                         newReward.points_required <= 500 ? 'Mittel' :
                                         newReward.points_required <= 1500 ? 'Schwer' :
                                         newReward.points_required <= 4000 ? 'Sehr schwer' :
                                         'Legendär'}
                                    </Badge>
                                </div>
                                <div className="flex justify-between text-[10px] text-muted-foreground">
                                    <span>0</span>
                                    <span>500</span>
                                    <span>1500</span>
                                    <span>4000</span>
                                    <span>8000</span>
                                </div>
                                <p className="text-xs text-muted-foreground bg-muted/40 rounded-lg p-2.5 leading-relaxed">
                                    <span className="font-medium text-foreground">Richtwerte: </span>
                                    ~<span className="font-medium">10 Pkt.</span>/Achievement, 
                                    ~<span className="font-medium">50-80 Pkt./Woche</span> bei aktiver Nutzung.
                                    {newReward.points_required > 0 && (
                                        <span> Entspricht ca. <span className="font-medium">
                                            {newReward.points_required <= 80 ? '~1 Woche' :
                                             newReward.points_required <= 160 ? '~2 Wochen' :
                                             newReward.points_required <= 320 ? '~1 Monat' :
                                             newReward.points_required <= 640 ? '~2 Monate' :
                                             newReward.points_required <= 1300 ? '~4 Monate' :
                                             newReward.points_required <= 2600 ? '~8 Monate' :
                                             newReward.points_required <= 4000 ? '~1 Jahr' :
                                             '~1,5+ Jahre'}
                                        </span>.</span>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateReward(false)}>Abbrechen</Button>
                        <Button onClick={handleCreateReward} disabled={!newReward.name}>Erstellen</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Adjust Points Modal */}
            <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Punkte anpassen für {selectedUser?.user.first_name} {selectedUser?.user.last_name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-muted-foreground">
                            Aktuelle Punkte: <strong>{selectedUser?.total_points}</strong>
                        </p>
                        <div>
                            <Label>Punkte (+ für hinzufügen, - für abziehen)</Label>
                            <Input
                                type="number"
                                value={adjustPoints.points}
                                onChange={(e) => setAdjustPoints({ ...adjustPoints, points: parseInt(e.target.value) || 0 })}
                                placeholder="z.B. 10 oder -5"
                            />
                        </div>
                        <div>
                            <Label>Grund</Label>
                            <Input
                                value={adjustPoints.reason}
                                onChange={(e) => setAdjustPoints({ ...adjustPoints, reason: e.target.value })}
                                placeholder="z.B. Bonus für Projekt X"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedUser(null)}>Abbrechen</Button>
                        <Button 
                            onClick={handleAdjustPoints}
                            disabled={!adjustPoints.points || !adjustPoints.reason}
                        >
                            Anwenden
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ChallengesPage;
